import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import * as bootstrap from 'bootstrap';
import * as helpscout from 'smg/admin/helpscout';
import * as hyejoo from 'smg/admin/hyejoo';
import { GetRealms } from 'smg/admin/realms';
import * as models from 'smg/admin/models';
import { ClearCache, HasPerm, Session } from 'smg/admin/session';
import axios from 'smg/axios';
import * as buttons from 'smg/buttons';
import { Copy } from 'smg/clipboard';
import { CSSToRGB, IsDark, Luminance } from 'smg/color';
import { ShowError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import { Phone } from 'smg/phone';
import { BlurSensitiveImages, SetBlurSensitiveImages } from 'smg/picture';
import * as toast from 'smg/toast';
import * as uploads from 'smg/uploads';
import { WeightedDefaultSalesTeams } from '../realms';

declare interface myWindow extends Window {
    readonly CloudFront: string;
    readonly profileImageSrc: string;
    readonly bootstrapHref?: string;
    readonly setTheme: (theme: string) => void;
    readonly getTheme: () => string;
    readonly applyThemeVisual: (theme: string) => void;
}

interface themeColors {
    readonly bg: string;
    readonly main: string;
    readonly sub: string;
    readonly subAlt: string;
    readonly text: string;
    readonly error: string;
    readonly success?: string;
    readonly warning?: string;
    readonly info?: string;
    readonly sidebarBg?: string;
    readonly sidebarColor?: string;
}

interface themeInfo {
    readonly name: string;
    readonly label: string;
    readonly dark: boolean;
    readonly colors?: themeColors; // absent only for auto, light, dark (Bootstrap native)
}

function themePreviewColors(t: themeInfo): [string, string, string] {
    if (t.colors) {
        return [t.colors.bg, t.colors.main, t.colors.text];
    }
    if (t.name === 'light') {
        return ['#ffffff', '#0d6efd', '#212529'];
    }
    return ['#212529', '#0d6efd', '#dee2e6']; // dark / auto
}

// All themes sorted alphabetically by label. Auto/Light/Dark are Bootstrap-native
// (no colors needed). Everything else applies CSS vars at runtime.
const themes: themeInfo[] = [
    { name: 'auto', label: 'Auto (System)', dark: false },
    { name: 'light', label: 'Light', dark: false },
    { name: 'dark', label: 'Dark', dark: true },
    { name: '8008', label: '8008', dark: true, colors: { bg: '#333a45', main: '#f44c7f', sub: '#939eae', subAlt: '#2e343d', text: '#e9ecf0', error: '#da3333' } },
    { name: '80s_after_dark', label: '80s after dark', dark: true, colors: { bg: '#1b1d36', main: '#fca6d1', sub: '#99d6ea', subAlt: '#17182c', text: '#e1e7ec', error: '#fffb85' } },
    { name: '9009', label: '9009', dark: false, colors: { bg: '#eeebe2', main: '#080909', sub: '#99947f', subAlt: '#d3cfc1', text: '#080909', error: '#c87e74' } },
    { name: 'aether', label: 'aether', dark: true, colors: { bg: '#101820', main: '#eedaea', sub: '#cf6bdd', subAlt: '#292136', text: '#eedaea', error: '#ff5253' } },
    { name: 'alduin', label: 'alduin', dark: true, colors: { bg: '#1c1c1c', main: '#dfd7af', sub: '#444444', subAlt: '#242424', text: '#f5f3ed', error: '#af5f5f' } },
    { name: 'alpine', label: 'alpine', dark: true, colors: { bg: '#6c687f', main: '#ffffff', sub: '#9994b8', subAlt: '#77738c', text: '#ffffff', error: '#e32b2b' } },
    { name: 'anti_hero', label: 'anti hero', dark: true, colors: { bg: '#00002e', main: '#ffadad', sub: '#ff3d8b', subAlt: '#060548', text: '#f1deef', error: '#8fecff' } },
    { name: 'arch', label: 'arch', dark: true, colors: { bg: '#0c0d11', main: '#7ebab5', sub: '#454864', subAlt: '#171a25', text: '#f6f5f5', error: '#ff4754' } },
    { name: 'aurora', label: 'aurora', dark: true, colors: { bg: '#011926', main: '#00e980', sub: '#245c69', subAlt: '#000c13', text: '#ffffff', error: '#b94da1' } },
    { name: 'beach', label: 'beach', dark: false, colors: { bg: '#ffeead', main: '#96ceb4', sub: '#ffcc5c', subAlt: '#f7dc8f', text: '#5b7869', error: '#ff6f69' } },
    { name: 'bento', label: 'bento', dark: true, colors: { bg: '#2d394d', main: '#ff7a90', sub: '#4a768d', subAlt: '#263041', text: '#fffaf8', error: '#ee2a3a' } },
    { name: 'bingsu', label: 'bingsu', dark: false, colors: { bg: '#b8a7aa', main: '#83616e', sub: '#48373d', subAlt: '#ab989e', text: '#ebe6ea', error: '#921341' } },
    { name: 'bliss', label: 'bliss', dark: true, colors: { bg: '#262727', main: '#f0d3c9', sub: '#665957', subAlt: '#343231', text: '#ffffff', error: '#bd4141' } },
    { name: 'blue_dolphin', label: 'blue dolphin', dark: true, colors: { bg: '#003950', main: '#ffcefb', sub: '#00e4ff', subAlt: '#014961', text: '#82eaff', error: '#ffbde6' } },
    { name: 'blueberry_dark', label: 'blueberry dark', dark: true, colors: { bg: '#212b42', main: '#add7ff', sub: '#5c7da5', subAlt: '#1b2334', text: '#91b4d5', error: '#df4576' } },
    { name: 'blueberry_light', label: 'blueberry light', dark: false, colors: { bg: '#dae0f5', main: '#506477', sub: '#92a4be', subAlt: '#c1c7df', text: '#678198', error: '#df4576' } },
    { name: 'botanical', label: 'botanical', dark: false, colors: { bg: '#7b9c98', main: '#eaf1f3', sub: '#495755', subAlt: '#72908d', text: '#eaf1f3', error: '#f6c9b4' } },
    { name: 'bouquet', label: 'bouquet', dark: true, colors: { bg: '#173f35', main: '#eaa09c', sub: '#408e7b', subAlt: '#1f4e43', text: '#e9e0d2', error: '#d44729' } },
    { name: 'breeze', label: 'breeze', dark: false, colors: { bg: '#e8d5c4', main: '#7d67a9', sub: '#3a98b9', subAlt: '#f6e6da', text: '#1b4c5e', error: '#7d67a9' } },
    { name: 'bushido', label: 'bushido', dark: true, colors: { bg: '#242933', main: '#ec4c56', sub: '#596172', subAlt: '#1c222d', text: '#f6f0e9', error: '#ec4c56' } },
    { name: 'cafe', label: 'cafe', dark: false, colors: { bg: '#ceb18d', main: '#14120f', sub: '#d4d2d1', subAlt: '#bba180', text: '#14120f', error: '#c82931' } },
    { name: 'camping', label: 'camping', dark: false, colors: { bg: '#faf1e4', main: '#618c56', sub: '#c2b8aa', subAlt: '#e7dccb', text: '#3c403b', error: '#ad4f4e' } },
    { name: 'carbon', label: 'carbon', dark: true, colors: { bg: '#313131', main: '#f66e0d', sub: '#616161', subAlt: '#2b2b2b', text: '#f5e6c8', error: '#e72d2d' } },
    { name: 'catppuccin', label: 'catppuccin', dark: true, colors: { bg: '#1e1e2e', main: '#cba6f7', sub: '#7f849c', subAlt: '#181825', text: '#cdd6f4', error: '#f38ba8' } },
    { name: 'chaos_theory', label: 'chaos theory', dark: true, colors: { bg: '#141221', main: '#fd77d7', sub: '#676e8a', subAlt: '#1e1d2f', text: '#dde5ed', error: '#fd77d7' } },
    { name: 'cheesecake', label: 'cheesecake', dark: false, colors: { bg: '#fdf0d5', main: '#8e2949', sub: '#d91c81', subAlt: '#f3e2bf', text: '#3a3335', error: '#5cf074' } },
    { name: 'cherry_blossom', label: 'cherry blossom', dark: true, colors: { bg: '#323437', main: '#d65ccc', sub: '#787d82', subAlt: '#2d2f31', text: '#d1d0c5', error: '#ca4754' } },
    { name: 'comfy', label: 'comfy', dark: true, colors: { bg: '#4a5b6e', main: '#f8cdc6', sub: '#9ec1cc', subAlt: '#425366', text: '#f5efee', error: '#c9465e' } },
    { name: 'copper', label: 'copper', dark: true, colors: { bg: '#442f29', main: '#b46a55', sub: '#7ebab5', subAlt: '#50362e', text: '#e7e0de', error: '#a32424' } },
    { name: 'creamsicle', label: 'creamsicle', dark: false, colors: { bg: '#ff9869', main: '#fcfcf8', sub: '#ff661f', subAlt: '#fe8954', text: '#fcfcf8', error: '#6a0dad' } },
    { name: 'cy_red', label: 'cy red', dark: true, colors: { bg: '#6e2626', main: '#e55050', sub: '#ff6060', subAlt: '#3f1616', text: '#ffaaaa', error: '#919fd9' } },
    { name: 'cyberspace', label: 'cyberspace', dark: true, colors: { bg: '#181c18', main: '#00ce7c', sub: '#9578d3', subAlt: '#131613', text: '#c2fbe1', error: '#ff5f5f' } },
    { name: 'dark_magic_girl', label: 'dark magic girl', dark: true, colors: { bg: '#091f2c', main: '#f5b1cc', sub: '#93e8d3', subAlt: '#071823', text: '#a288d9', error: '#e45c96' } },
    { name: 'dark_note', label: 'dark note', dark: true, colors: { bg: '#1f1f1f', main: '#f2c17b', sub: '#768f95', subAlt: '#141414', text: '#d2dff4', error: '#ff0000' } },
    { name: 'darling', label: 'darling', dark: false, colors: { bg: '#fec8cd', main: '#ffffff', sub: '#a30000', subAlt: '#f2babd', text: '#ffffff', error: '#2e7dde' } },
    { name: 'deku', label: 'deku', dark: true, colors: { bg: '#058b8c', main: '#b63530', sub: '#255458', subAlt: '#0e7d7e', text: '#f7f2ea', error: '#b63530' } },
    { name: 'desert_oasis', label: 'desert oasis', dark: false, colors: { bg: '#fff2d5', main: '#d19d01', sub: '#0061fe', subAlt: '#eddebc', text: '#332800', error: '#76bb40' } },
    { name: 'dev', label: 'dev', dark: true, colors: { bg: '#1b2028', main: '#23a9d5', sub: '#4b5975', subAlt: '#151a21', text: '#ccccb5', error: '#b81b2c' } },
    { name: 'diner', label: 'diner', dark: true, colors: { bg: '#537997', main: '#c3af5b', sub: '#445c7f', subAlt: '#4d6f8b', text: '#dfdbc8', error: '#ad5145' } },
    { name: 'dino', label: 'dino', dark: false, colors: { bg: '#ffffff', main: '#40d672', sub: '#d5d5d5', subAlt: '#cafad8', text: '#1d221f', error: '#ff5f5f' } },
    { name: 'discord', label: 'discord', dark: true, colors: { bg: '#313338', main: '#5a65ea', sub: '#565861', subAlt: '#2b2d31', text: '#dcdee3', error: '#df4f4b' } },
    { name: 'dmg', label: 'dmg', dark: false, colors: { bg: '#dadbdc', main: '#ae185e', sub: '#3846b1', subAlt: '#bec1d2', text: '#414141', error: '#ae185e' } },
    { name: 'dollar', label: 'dollar', dark: false, colors: { bg: '#e4e4d4', main: '#6b886b', sub: '#8a9b69', subAlt: '#cbd0bf', text: '#555a56', error: '#d60000' } },
    { name: 'dots', label: 'dots', dark: true, colors: { bg: '#121520', main: '#ffffff', sub: '#676e8a', subAlt: '#1b1e2c', text: '#ffffff', error: '#da3333' } },
    { name: 'dracula', label: 'Dracula', dark: true, colors: { bg: '#282a36', main: '#bd93f9', sub: '#6272a4', subAlt: '#44475a', text: '#f8f8f2', error: '#ff5555' } },
    { name: 'drowning', label: 'drowning', dark: true, colors: { bg: '#191826', main: '#4a6fb5', sub: '#50688c', subAlt: '#1e1f2f', text: '#9393a7', error: '#be555f' } },
    { name: 'dualshot', label: 'dualshot', dark: true, colors: { bg: '#737373', main: '#212222', sub: '#aaaaaa', subAlt: '#646464', text: '#212222', error: '#c82931' } },
    { name: 'earthsong', label: 'Earthsong', dark: true, colors: { bg: '#36312c', main: '#f8bb39', sub: '#7a7267', subAlt: '#26231f', text: '#ebd1b7', error: '#fe4450' } },
    { name: 'everblush', label: 'everblush', dark: true, colors: { bg: '#141b1e', main: '#8ccf7e', sub: '#838887', subAlt: '#232a2d', text: '#dadada', error: '#e57474' } },
    { name: 'evil_eye', label: 'evil eye', dark: true, colors: { bg: '#0084c2', main: '#f7f2ea', sub: '#01589f', subAlt: '#0c79be', text: '#171718', error: '#ca4754' } },
    { name: 'ez_mode', label: 'ez mode', dark: true, colors: { bg: '#0068c6', main: '#fa62d5', sub: '#138bf7', subAlt: '#005bac', text: '#ffffff', error: '#4ddb47' } },
    { name: 'fire', label: 'fire', dark: true, colors: { bg: '#0f0000', main: '#b31313', sub: '#683434', subAlt: '#200a0a', text: '#ffffff', error: '#2f3cb6' } },
    { name: 'fledgling', label: 'fledgling', dark: true, colors: { bg: '#3b363f', main: '#fc6e83', sub: '#8e5568', subAlt: '#332e38', text: '#e6d5d3', error: '#f52443' } },
    { name: 'fleuriste', label: 'fleuriste', dark: false, colors: { bg: '#c6b294', main: '#405a52', sub: '#64374d', subAlt: '#b4a389', text: '#091914', error: '#990000' } },
    { name: 'floret', label: 'floret', dark: true, colors: { bg: '#00272c', main: '#ffdd6d', sub: '#779097', subAlt: '#173033', text: '#e5e5e5', error: '#8a4000' } },
    { name: 'froyo', label: 'froyo', dark: false, colors: { bg: '#e1dacb', main: '#7b7d7d', sub: '#b29c5e', subAlt: '#d3cdc1', text: '#7b7d7d', error: '#f28578' } },
    { name: 'frozen_llama', label: 'frozen llama', dark: false, colors: { bg: '#9bf2ea', main: '#6d44a6', sub: '#b690fd', subAlt: '#7fe7dd', text: '#ffffff', error: '#e42629' } },
    { name: 'fruit_chew', label: 'fruit chew', dark: false, colors: { bg: '#d6d3d6', main: '#5c1e5f', sub: '#b49cb5', subAlt: '#cabfca', text: '#282528', error: '#bd2621' } },
    { name: 'fundamentals', label: 'fundamentals', dark: true, colors: { bg: '#727474', main: '#7fa482', sub: '#cac4be', subAlt: '#666868', text: '#131313', error: '#5e477c' } },
    { name: 'future_funk', label: 'future funk', dark: true, colors: { bg: '#2e1a47', main: '#f7f2ea', sub: '#c18fff', subAlt: '#27173c', text: '#f7f2ea', error: '#f04e98' } },
    { name: 'github', label: 'github', dark: true, colors: { bg: '#212830', main: '#41ce5c', sub: '#788386', subAlt: '#141b23', text: '#ccdae6', error: '#c23e3a' } },
    { name: 'github-dark', label: 'GitHub Dark', dark: true, colors: { bg: '#0d1117', main: '#4493f8', sub: '#8b949e', subAlt: '#161b22', text: '#e6edf3', error: '#f85149' } },
    {
        name: 'github-cb',
        label: 'GitHub Dark Colorblind',
        dark: true,
        colors: {
            bg: '#0d1117',
            main: '#4493f8',
            sub: '#8b949e',
            subAlt: '#161b22',
            text: '#e6edf3',
            error: '#f85149',
            success: '#4493f8',
        },
    },
    { name: 'github-light', label: 'GitHub Light', dark: false, colors: { bg: '#ffffff', main: '#0969da', sub: '#656d76', subAlt: '#f6f8fa', text: '#1f2328', error: '#d1242f' } },
    { name: 'github-purple', label: 'GitHub Purple', dark: true, colors: { bg: '#0d1117', main: '#6940c5', sub: '#60666b', subAlt: '#161b22', text: '#e1e4e8', error: '#f97583' } },
    { name: 'godspeed', label: 'godspeed', dark: false, colors: { bg: '#eae4cf', main: '#9abbcd', sub: '#ada998', subAlt: '#ded9c9', text: '#646669', error: '#ca4754' } },
    { name: 'graen', label: 'graen', dark: true, colors: { bg: '#303c36', main: '#a59682', sub: '#181d1a', subAlt: '#36453c', text: '#a59682', error: '#601420' } },
    { name: 'grand_prix', label: 'grand prix', dark: true, colors: { bg: '#36475c', main: '#c0d036', sub: '#5c6c80', subAlt: '#42536b', text: '#c1c7d7', error: '#fc5727' } },
    { name: 'grape', label: 'grape', dark: true, colors: { bg: '#2c003e', main: '#ff8f00', sub: '#6e225e', subAlt: '#1f002d', text: '#ffffff', error: '#ff4081' } },
    { name: 'gruvbox-dark', label: 'Gruvbox Dark', dark: true, colors: { bg: '#282828', main: '#83a598', sub: '#665c54', subAlt: '#3c3836', text: '#ebdbb2', error: '#fb4934' } },
    { name: 'gruvbox-light', label: 'Gruvbox Light', dark: false, colors: { bg: '#fbf1c7', main: '#83a598', sub: '#bdae93', subAlt: '#ebdbb2', text: '#3c3836', error: '#fb4934' } },
    { name: 'hammerhead', label: 'hammerhead', dark: true, colors: { bg: '#030613', main: '#4fcdb9', sub: '#213c53', subAlt: '#0a1928', text: '#e2f1f5', error: '#e32b2b' } },
    { name: 'hanok', label: 'hanok', dark: false, colors: { bg: '#d8d2c3', main: '#513a2a', sub: '#8b6f5c', subAlt: '#cdc0af', text: '#393b3b', error: '#ca4754' } },
    { name: 'hedge', label: 'hedge', dark: true, colors: { bg: '#415e31', main: '#6a994e', sub: '#ede5b4', subAlt: '#38502a', text: '#f7f1d6', error: '#ca3d3f' } },
    { name: 'honey', label: 'honey', dark: false, colors: { bg: '#f2aa00', main: '#ffffff', sub: '#a66b00', subAlt: '#e19e00', text: '#f3eecb', error: '#df3333' } },
    { name: 'horizon', label: 'horizon', dark: true, colors: { bg: '#1c1e26', main: '#c4a88a', sub: '#db886f', subAlt: '#17181f', text: '#bbbbbb', error: '#d55170' } },
    { name: 'hotdog', label: 'Hotdog Stand', dark: true, colors: { bg: '#ff0000', main: '#ffff00', sub: '#cc0000', subAlt: '#cc0000', text: '#ffff00', error: '#000000' } },
    { name: 'husqy', label: 'husqy', dark: true, colors: { bg: '#000000', main: '#c58aff', sub: '#972fff', subAlt: '#1e001e', text: '#ebd7ff', error: '#da3333' } },
    { name: 'iceberg_dark', label: 'iceberg dark', dark: true, colors: { bg: '#161821', main: '#84a0c6', sub: '#595e76', subAlt: '#232531', text: '#c6c8d1', error: '#e27878' } },
    { name: 'iceberg_light', label: 'iceberg light', dark: false, colors: { bg: '#e8e9ec', main: '#2d539e', sub: '#adb1c4', subAlt: '#ccceda', text: '#33374c', error: '#cc517a' } },
    { name: 'incognito', label: 'incognito', dark: true, colors: { bg: '#0e0e0e', main: '#ff9900', sub: '#555555', subAlt: '#151515', text: '#c6c6c6', error: '#e44545' } },
    { name: 'ishtar', label: 'ishtar', dark: true, colors: { bg: '#202020', main: '#91170c', sub: '#847869', subAlt: '#272727', text: '#fae1c3', error: '#bb1e10' } },
    { name: 'iv_clover', label: 'iv clover', dark: false, colors: { bg: '#a0a0a0', main: '#573e40', sub: '#353535', subAlt: '#bebebe', text: '#3b2d3b', error: '#937173' } },
    { name: 'iv_spade', label: 'iv spade', dark: true, colors: { bg: '#0c0c0c', main: '#b7976a', sub: '#404040', subAlt: '#121212', text: '#d3c2c3', error: '#9d7b7d' } },
    { name: 'joker', label: 'joker', dark: true, colors: { bg: '#1a0e25', main: '#99de1e', sub: '#7554a3', subAlt: '#14081f', text: '#e9e2f5', error: '#e32b2b' } },
    { name: 'kimbie-dark', label: 'Kimbie Dark', dark: true, colors: { bg: '#221a0f', main: '#8ab1b0', sub: '#a57a4c', subAlt: '#362712', text: '#d3af86', error: '#dc3958' } },
    { name: 'laser', label: 'laser', dark: true, colors: { bg: '#221b44', main: '#009eaf', sub: '#b82356', subAlt: '#1e173b', text: '#dbe7e8', error: '#a8d400' } },
    { name: 'lavender', label: 'lavender', dark: false, colors: { bg: '#ada6c2', main: '#e4e3e9', sub: '#e4e3e9', subAlt: '#a19bb9', text: '#2f2a41', error: '#ca4754' } },
    { name: 'leather', label: 'leather', dark: true, colors: { bg: '#a86948', main: '#ffe4bc', sub: '#81482b', subAlt: '#9a5f3f', text: '#ffe4bc', error: '#ca4754' } },
    { name: 'lil_dragon', label: 'lil dragon', dark: false, colors: { bg: '#ebe1ef', main: '#8a5bd6', sub: '#a28db8', subAlt: '#dac7e2', text: '#212b43', error: '#f794ca' } },
    { name: 'lilac_mist', label: 'lilac mist', dark: false, colors: { bg: '#fffbfe', main: '#b94189', sub: '#e094c2', subAlt: '#ecdcee', text: '#5c2954', error: '#ff6f69' } },
    { name: 'lime', label: 'lime', dark: true, colors: { bg: '#7c878e', main: '#93c247', sub: '#4b5257', subAlt: '#737d82', text: '#bfcfdc', error: '#ea4221' } },
    { name: 'luna', label: 'luna', dark: true, colors: { bg: '#221c35', main: '#f67599', sub: '#5a3a7e', subAlt: '#2f2346', text: '#ffe3eb', error: '#efc050' } },
    { name: 'macroblank', label: 'macroblank', dark: false, colors: { bg: '#b2d2c8', main: '#c13117', sub: '#717977', subAlt: '#c6ddd3', text: '#490909', error: '#c13117' } },
    { name: 'magic_girl', label: 'magic girl', dark: false, colors: { bg: '#ffffff', main: '#f5b1cc', sub: '#93e8d3', subAlt: '#f2f2f2', text: '#00ac8c', error: '#ffe495' } },
    { name: 'mashu', label: 'mashu', dark: true, colors: { bg: '#2b2b2c', main: '#76689a', sub: '#d8a0a6', subAlt: '#27242c', text: '#f1e2e4', error: '#d44729' } },
    { name: 'matcha_moccha', label: 'matcha moccha', dark: true, colors: { bg: '#523525', main: '#7ec160', sub: '#9e6749', subAlt: '#422b1e', text: '#ecddcc', error: '#fb4934' } },
    { name: 'material', label: 'material', dark: true, colors: { bg: '#263238', main: '#80cbc4', sub: '#4c6772', subAlt: '#2e3c43', text: '#e6edf3', error: '#fb4934' } },
    { name: 'matrix', label: 'matrix', dark: true, colors: { bg: '#000000', main: '#15ff00', sub: '#006500', subAlt: '#032000', text: '#d1ffcd', error: '#da3333' } },
    { name: 'menthol', label: 'menthol', dark: false, colors: { bg: '#00c18c', main: '#ffffff', sub: '#186544', subAlt: '#17ae7d', text: '#ffffff', error: '#e03c3c' } },
    { name: 'metaverse', label: 'metaverse', dark: true, colors: { bg: '#232323', main: '#d82934', sub: '#5e5e5e', subAlt: '#1d1d1d', text: '#e8e8e8', error: '#da3333' } },
    { name: 'metropolis', label: 'metropolis', dark: true, colors: { bg: '#0f1f2c', main: '#56c3b7', sub: '#326984', subAlt: '#0b1822', text: '#e4edf1', error: '#d44729' } },
    {
        name: 'mets_away',
        label: 'Mets - Away',
        dark: false,
        colors: {
            bg: '#c4c2c2',
            main: '#ff5910',
            sub: '#002d72',
            subAlt: '#b5b3b3',
            text: '#000000',
            error: '#d63333',
            sidebarBg: '#002d72',
            sidebarColor: '#ffffff',
        },
    },
    {
        name: 'mets_home',
        label: 'Mets - Home',
        dark: true,
        colors: {
            bg: '#262423',
            main: '#ff5910',
            sub: '#c4c2c2',
            subAlt: '#1c1b1a',
            text: '#c4c2c2',
            error: '#d63333',
            sidebarBg: '#002d72',
            sidebarColor: '#ffffff',
        },
    },
    { name: 'mexican', label: 'mexican', dark: false, colors: { bg: '#f8ad34', main: '#b12189', sub: '#333333', subAlt: '#f9b951', text: '#eeeeee', error: '#da3333' } },
    { name: 'miami', label: 'miami', dark: false, colors: { bg: '#f35588', main: '#05dfd7', sub: '#94294c', subAlt: '#db4979', text: '#f0e9ec', error: '#fff591' } },
    { name: 'miami_nights', label: 'miami nights', dark: true, colors: { bg: '#18181a', main: '#e4609b', sub: '#47bac0', subAlt: '#0f0f10', text: '#ffffff', error: '#fff591' } },
    { name: 'midnight', label: 'midnight', dark: true, colors: { bg: '#0b0e13', main: '#60759f', sub: '#394760', subAlt: '#141a24', text: '#9fadc6', error: '#c27070' } },
    { name: 'milkshake', label: 'milkshake', dark: false, colors: { bg: '#ffffff', main: '#212b43', sub: '#62cfe6', subAlt: '#ddeff3', text: '#212b43', error: '#f19dac' } },
    { name: 'mint', label: 'mint', dark: true, colors: { bg: '#05385b', main: '#5cdb95', sub: '#20688a', subAlt: '#07324e', text: '#edf5e1', error: '#f35588' } },
    { name: 'mizu', label: 'mizu', dark: false, colors: { bg: '#afcbdd', main: '#fcfbf6', sub: '#85a5bb', subAlt: '#9fc1d4', text: '#1a2633', error: '#bf616a' } },
    { name: 'modern_dolch', label: 'modern dolch', dark: true, colors: { bg: '#2d2e30', main: '#7eddd3', sub: '#54585c', subAlt: '#242527', text: '#e3e6eb', error: '#d36a7b' } },
    { name: 'modern_dolch_light', label: 'modern dolch light', dark: false, colors: { bg: '#dbdbdb', main: '#8fd1c3', sub: '#a3a2a2', subAlt: '#e8e8e8', text: '#454545', error: '#ea8a9a' } },
    { name: 'modern_ink', label: 'modern ink', dark: false, colors: { bg: '#ffffff', main: '#ff360d', sub: '#b7b7b7', subAlt: '#ececec', text: '#000000', error: '#d70000' } },
    { name: 'monokai', label: 'Monokai Pro Octagon', dark: true, colors: { bg: '#282a3a', main: '#9cd1bb', sub: '#696d77', subAlt: '#363750', text: '#eaf2f1', error: '#ff657a' } },
    { name: 'moonlight', label: 'moonlight', dark: true, colors: { bg: '#191f28', main: '#c69f68', sub: '#4b5975', subAlt: '#141a22', text: '#ccccb5', error: '#b81b2c' } },
    { name: 'mountain', label: 'mountain', dark: true, colors: { bg: '#0f0f0f', main: '#e7e7e7', sub: '#4c4c4c', subAlt: '#1a1a1a', text: '#e7e7e7', error: '#ac8c8c' } },
    { name: 'mr_sleeves', label: 'mr sleeves', dark: false, colors: { bg: '#d1d7da', main: '#daa99b', sub: '#9a9fa1', subAlt: '#bfcbd1', text: '#1d1d1d', error: '#bf6464' } },
    { name: 'ms_cupcakes', label: 'ms cupcakes', dark: false, colors: { bg: '#ffffff', main: '#5ed5f3', sub: '#d64090', subAlt: '#edf8fa', text: '#0a282f', error: '#a4dd32' } },
    { name: 'muted', label: 'muted', dark: true, colors: { bg: '#525252', main: '#c5b4e3', sub: '#939eae', subAlt: '#494949', text: '#b1e4e3', error: '#edc1cd' } },
    { name: 'nautilus', label: 'nautilus', dark: true, colors: { bg: '#132237', main: '#ebb723', sub: '#0b4c6c', subAlt: '#0e1a29', text: '#1cbaac', error: '#da3333' } },
    { name: 'nebula', label: 'nebula', dark: true, colors: { bg: '#212135', main: '#be3c88', sub: '#19b3b8', subAlt: '#191928', text: '#838686', error: '#ca4754' } },
    { name: 'night_runner', label: 'night runner', dark: true, colors: { bg: '#212121', main: '#feff04', sub: '#5c4a9c', subAlt: '#1a1a1a', text: '#e8e8e8', error: '#da3333' } },
    { name: 'nord', label: 'Nord', dark: true, colors: { bg: '#2e3440', main: '#81a1c1', sub: '#4c566a', subAlt: '#3b4252', text: '#d8dee9', error: '#bf616a' } },
    { name: 'norse', label: 'norse', dark: true, colors: { bg: '#242425', main: '#2b5f6d', sub: '#505b5e', subAlt: '#303333', text: '#ccc2b1', error: '#7e2a2a' } },
    { name: 'oblivion', label: 'oblivion', dark: true, colors: { bg: '#313231', main: '#a5a096', sub: '#5d6263', subAlt: '#3a3b3b', text: '#f7f5f1', error: '#dd452e' } },
    { name: 'olive', label: 'olive', dark: false, colors: { bg: '#e9e5cc', main: '#92946f', sub: '#b7b39e', subAlt: '#d4cfbc', text: '#373731', error: '#cf2f2f' } },
    { name: 'olivia', label: 'olivia', dark: true, colors: { bg: '#1c1b1d', main: '#deaf9d', sub: '#4e3e3e', subAlt: '#262223', text: '#f2efed', error: '#bf616a' } },
    { name: 'onedark', label: 'onedark', dark: true, colors: { bg: '#2f343f', main: '#61afef', sub: '#eceff4', subAlt: '#262b34', text: '#98c379', error: '#e06c75' } },
    { name: 'our_theme', label: 'our theme', dark: true, colors: { bg: '#ce1226', main: '#fcd116', sub: '#6d0f19', subAlt: '#9f1020', text: '#ffffff', error: '#fcd116' } },
    { name: 'pale_nimbus', label: 'pale nimbus', dark: true, colors: { bg: '#433e4c', main: '#94ffc2', sub: '#ffaca3', subAlt: '#694f5e', text: '#feffdb', error: '#ff5c5c' } },
    { name: 'paper', label: 'paper', dark: false, colors: { bg: '#eeeeee', main: '#444444', sub: '#b2b2b2', subAlt: '#dddddd', text: '#444444', error: '#d70000' } },
    { name: 'passion_fruit', label: 'passion fruit', dark: true, colors: { bg: '#7c2142', main: '#f4a3b4', sub: '#9994b8', subAlt: '#833c5e', text: '#ffffff', error: '#deb80b' } },
    { name: 'pastel', label: 'pastel', dark: false, colors: { bg: '#e0b2bd', main: '#fbf4b6', sub: '#b4e9ff', subAlt: '#d29fab', text: '#6d5c6f', error: '#ff6961' } },
    { name: 'peach_blossom', label: 'peach blossom', dark: true, colors: { bg: '#292929', main: '#99b898', sub: '#616161', subAlt: '#2a363b', text: '#fecea8', error: '#ff6961' } },
    { name: 'peaches', label: 'peaches', dark: false, colors: { bg: '#e0d7c1', main: '#dd7a5f', sub: '#e7b28e', subAlt: '#e2caaf', text: '#5f4c41', error: '#ff6961' } },
    { name: 'phantom', label: 'phantom', dark: true, colors: { bg: '#000011', main: '#7aa2f7', sub: '#414868', subAlt: '#24283b', text: '#c0caf5', error: '#f7768e' } },
    { name: 'pink_lemonade', label: 'pink lemonade', dark: false, colors: { bg: '#f6d992', main: '#f6a192', sub: '#f6b092', subAlt: '#f6cc93', text: '#fcfcf8', error: '#ff6f69' } },
    { name: 'pulse', label: 'pulse', dark: true, colors: { bg: '#181818', main: '#17b8bd', sub: '#53565a', subAlt: '#121212', text: '#e5f4f4', error: '#da3333' } },
    { name: 'purpleish', label: 'purpleish', dark: true, colors: { bg: '#1e1e32', main: '#7a52cc', sub: '#5c5c99', subAlt: '#181829', text: '#a3a3cc', error: '#ff6666' } },
    { name: 'rainbow_trail', label: 'rainbow trail', dark: false, colors: { bg: '#f5f5f5', main: '#363636', sub: '#4f4f4f', subAlt: '#e0e0e0', text: '#1f1f1f', error: '#ff0008' } },
    { name: 'red', label: 'Red', dark: true, colors: { bg: '#1c0a0a', main: '#ff3434', sub: '#8a5555', subAlt: '#2a1414', text: '#e8d0d0', error: '#ff3434' } },
    { name: 'red_dragon', label: 'red dragon', dark: true, colors: { bg: '#1a0b0c', main: '#ff3a32', sub: '#e2a528', subAlt: '#0e0506', text: '#4a4d4e', error: '#771b1f' } },
    { name: 'red_samurai', label: 'red samurai', dark: true, colors: { bg: '#84202c', main: '#c79e6e', sub: '#55131b', subAlt: '#751d26', text: '#e2dad0', error: '#33bbda' } },
    { name: 'repose_dark', label: 'repose dark', dark: true, colors: { bg: '#2f3338', main: '#d6d2bc', sub: '#8f8e84', subAlt: '#3a3c3d', text: '#d6d2bc', error: '#ff4a59' } },
    { name: 'repose_light', label: 'repose light', dark: false, colors: { bg: '#efead0', main: '#5f605e', sub: '#8f8e84', subAlt: '#dbd6c4', text: '#333538', error: '#c43c53' } },
    { name: 'retro', label: 'retro', dark: false, colors: { bg: '#dad3c1', main: '#1d1b17', sub: '#918b7d', subAlt: '#c8c3b3', text: '#1d1b17', error: '#bf616a' } },
    { name: 'retrocast', label: 'retrocast', dark: true, colors: { bg: '#07737a', main: '#88dbdf', sub: '#f3e03b', subAlt: '#26858b', text: '#ffffff', error: '#ff585d' } },
    { name: 'rose_pine', label: 'rose pine', dark: true, colors: { bg: '#1f1d27', main: '#9ccfd8', sub: '#c4a7e7', subAlt: '#282533', text: '#e0def4', error: '#eb6f92' } },
    { name: 'rose_pine_dawn', label: 'rose pine dawn', dark: false, colors: { bg: '#fffaf3', main: '#56949f', sub: '#c4a7e7', subAlt: '#f0e9df', text: '#286983', error: '#b4637a' } },
    { name: 'rose_pine_moon', label: 'rose pine moon', dark: true, colors: { bg: '#2a273f', main: '#9ccfd8', sub: '#c4a7e7', subAlt: '#211f32', text: '#e0def4', error: '#eb6f92' } },
    { name: 'rudy', label: 'rudy', dark: true, colors: { bg: '#1a2b3e', main: '#af8f5c', sub: '#3a506c', subAlt: '#152231', text: '#c9c8bf', error: '#bf616a' } },
    { name: 'ryujinscales', label: 'ryujinscales', dark: true, colors: { bg: '#081426', main: '#f17754', sub: '#ffbc90', subAlt: '#040e1d', text: '#ffe4bc', error: '#ca4754' } },
    { name: 'serika', label: 'serika', dark: false, colors: { bg: '#e1e1e3', main: '#e2b714', sub: '#aaaeb3', subAlt: '#d1d3d8', text: '#323437', error: '#da3333' } },
    { name: 'serika_dark', label: 'serika dark', dark: true, colors: { bg: '#323437', main: '#e2b714', sub: '#646669', subAlt: '#2c2e31', text: '#d1d0c5', error: '#ca4754' } },
    { name: 'sewing_tin', label: 'sewing tin', dark: true, colors: { bg: '#241963', main: '#f2ce83', sub: '#446ad5', subAlt: '#2a277a', text: '#ffffff', error: '#c6915e' } },
    { name: 'sewing_tin_light', label: 'sewing tin light', dark: false, colors: { bg: '#ffffff', main: '#2d2076', sub: '#385eca', subAlt: '#c8cedf', text: '#2d2076', error: '#f2ce83' } },
    { name: 'shades-of-purple', label: 'Shades of Purple', dark: true, colors: { bg: '#2d2b55', main: '#fad000', sub: '#a599e9', subAlt: '#222244', text: '#ffffff', error: '#ec3a37' } },
    {
        name: 'shades-of-purple-sd',
        label: 'Shades of Purple (Super Dark)',
        dark: true,
        colors: {
            bg: '#191830',
            main: '#fad000',
            sub: '#a599e9',
            subAlt: '#131327',
            text: '#ffffff',
            error: '#ec3a37',
        },
    },
    { name: 'shoko', label: 'shoko', dark: false, colors: { bg: '#ced7e0', main: '#81c4dd', sub: '#7599b1', subAlt: '#b7cada', text: '#3b4c58', error: '#bf616a' } },
    { name: 'slambook', label: 'slambook', dark: false, colors: { bg: '#fffdde', main: '#03001c', sub: '#1c82ad', subAlt: '#c6dce4', text: '#13005a', error: '#f900bf' } },
    { name: 'snes', label: 'snes', dark: false, colors: { bg: '#bfbec2', main: '#553d94', sub: '#9f8ad4', subAlt: '#b5b0c2', text: '#2e2e2e', error: '#ca4754' } },
    { name: 'soaring_skies', label: 'soaring skies', dark: false, colors: { bg: '#fff9f2', main: '#55c6f0', sub: '#1e107a', subAlt: '#e5ddd4', text: '#1d1e1e', error: '#fb5745' } },
    { name: 'solarized-dark', label: 'Solarized Dark', dark: true, colors: { bg: '#002b36', main: '#268bd2', sub: '#586e75', subAlt: '#073642', text: '#839496', error: '#dc322f' } },
    { name: 'solarized-light', label: 'Solarized Light', dark: false, colors: { bg: '#fdf6e3', main: '#268bd2', sub: '#93a1a1', subAlt: '#eee8d5', text: '#657b83', error: '#dc322f' } },
    { name: 'sonokai', label: 'sonokai', dark: true, colors: { bg: '#2c2e34', main: '#9ed072', sub: '#e7c664', subAlt: '#232429', text: '#e2e2e3', error: '#fc5d7c' } },
    { name: 'spiderman', label: 'spiderman', dark: true, colors: { bg: '#0d1219', main: '#e23636', sub: '#0476f2', subAlt: '#0b1c2e', text: '#f0f0f0', error: '#0476f2' } },
    { name: 'stealth', label: 'stealth', dark: true, colors: { bg: '#010203', main: '#383e42', sub: '#5e676e', subAlt: '#121212', text: '#383e42', error: '#e25303' } },
    { name: 'strawberry', label: 'strawberry', dark: false, colors: { bg: '#f37f83', main: '#fcfcf8', sub: '#e53c58', subAlt: '#ef6e77', text: '#fcfcf8', error: '#fcd23f' } },
    { name: 'striker', label: 'striker', dark: true, colors: { bg: '#124883', main: '#d7dcda', sub: '#0f2d4e', subAlt: '#104176', text: '#d6dbd9', error: '#fb4934' } },
    { name: 'suisei', label: 'suisei', dark: true, colors: { bg: '#3b4a62', main: '#bef0ff', sub: '#fe9841', subAlt: '#313e55', text: '#dbdeeb', error: '#ed2939' } },
    { name: 'sunset', label: 'sunset', dark: true, colors: { bg: '#211e24', main: '#f79777', sub: '#5b578e', subAlt: '#161319', text: '#f4e0c9', error: '#66a1ff' } },
    { name: 'superuser', label: 'superuser', dark: true, colors: { bg: '#262a33', main: '#43ffaf', sub: '#526777', subAlt: '#1f232c', text: '#e5f7ef', error: '#ff5f5f' } },
    { name: 'sweden', label: 'sweden', dark: true, colors: { bg: '#0058a3', main: '#ffcc02', sub: '#57abdb', subAlt: '#024f8e', text: '#ffffff', error: '#e74040' } },
    { name: 'tangerine', label: 'tangerine', dark: false, colors: { bg: '#ffede0', main: '#fe5503', sub: '#ff9562', subAlt: '#fdd3bf', text: '#3d1705', error: '#7fb500' } },
    { name: 'taro', label: 'taro', dark: false, colors: { bg: '#b3baff', main: '#130f1a', sub: '#6f6c91', subAlt: '#a3a7df', text: '#130f1a', error: '#ffe23e' } },
    { name: 'terminal', label: 'terminal', dark: true, colors: { bg: '#191a1b', main: '#79a617', sub: '#48494b', subAlt: '#141516', text: '#e7eae0', error: '#a61717' } },
    { name: 'terra', label: 'terra', dark: true, colors: { bg: '#0c100e', main: '#89c559', sub: '#436029', subAlt: '#0f1d18', text: '#f0edd1', error: '#d3ca78' } },
    { name: 'terrazzo', label: 'terrazzo', dark: false, colors: { bg: '#f1e5da', main: '#e0794e', sub: '#688e8f', subAlt: '#e3d3c6', text: '#023e3b', error: '#a01034' } },
    { name: 'terror_below', label: 'terror below', dark: true, colors: { bg: '#0b1e1a', main: '#66ac92', sub: '#015c53', subAlt: '#041715', text: '#dceae5', error: '#bf616a' } },
    { name: 'tiramisu', label: 'tiramisu', dark: false, colors: { bg: '#cfc6b9', main: '#c0976f', sub: '#c0976f', subAlt: '#d0bca7', text: '#7d5448', error: '#e9632d' } },
    { name: 'tomorrow-night-blue', label: 'Tomorrow Night Blue', dark: true, colors: { bg: '#002451', main: '#bbdaff', sub: '#7285b7', subAlt: '#003f8e', text: '#ffffff', error: '#ff9da4' } },
    { name: 'trackday', label: 'trackday', dark: true, colors: { bg: '#464d66', main: '#e0513e', sub: '#5c7eb9', subAlt: '#3d4359', text: '#cfcfcf', error: '#e44e4e' } },
    { name: 'trance', label: 'trance', dark: true, colors: { bg: '#00021b', main: '#e51376', sub: '#3c4c79', subAlt: '#18214c', text: '#ffffff', error: '#02d3b0' } },
    { name: 'tron_orange', label: 'tron orange', dark: true, colors: { bg: '#0d1c1c', main: '#f0e800', sub: '#ff6600', subAlt: '#9c9191', text: '#ffffff', error: '#ff0000' } },
    { name: 'ubuntu', label: 'Ubuntu', dark: true, colors: { bg: '#2c001e', main: '#e95420', sub: '#aea79f', subAlt: '#3b0b2d', text: '#f0f0f0', error: '#c01c28' } },
    { name: 'ubuntu-highlight', label: 'Ubuntu Dark Highlight', dark: true, colors: { bg: '#262626', main: '#ee8241', sub: '#6d6d6d', subAlt: '#212121', text: '#eeffff', error: '#f44747' } },
    { name: 'ubuntu-deep', label: 'Ubuntu Deep Dark', dark: true, colors: { bg: '#131313', main: '#ee8241', sub: '#6d6d6d', subAlt: '#161616', text: '#eeffff', error: '#f44747' } },
    { name: 'ubuntu-soft', label: 'Ubuntu Soft Dark', dark: true, colors: { bg: '#222222', main: '#ee8241', sub: '#6d6d6d', subAlt: '#1e1c19', text: '#eeffff', error: '#f44747' } },
    { name: 'vaporwave', label: 'vaporwave', dark: false, colors: { bg: '#a4a7ea', main: '#e368da', sub: '#7c7faf', subAlt: '#989bd9', text: '#f1ebf1', error: '#573ca9' } },
    { name: 'vesper', label: 'vesper', dark: true, colors: { bg: '#101010', main: '#ffc799', sub: '#a0a0a0', subAlt: '#1c1c1c', text: '#ffffff', error: '#ff8080' } },
    { name: 'vesper_light', label: 'vesper light', dark: false, colors: { bg: '#ffffff', main: '#fb7100', sub: '#a0a0a0', subAlt: '#fff8f4', text: '#000000', error: '#ed2839' } },
    { name: 'viridescent', label: 'viridescent', dark: true, colors: { bg: '#2c3333', main: '#95d5b2', sub: '#84a98c', subAlt: '#232828', text: '#e9f5db', error: '#ff4646' } },
    { name: 'voc', label: 'voc', dark: true, colors: { bg: '#190618', main: '#e0caac', sub: '#4c1e48', subAlt: '#2c0c28', text: '#eeeae4', error: '#af3735' } },
    { name: 'watermelon', label: 'watermelon', dark: true, colors: { bg: '#1f4437', main: '#d6686f', sub: '#3e7a65', subAlt: '#244d3f', text: '#cdc6bc', error: '#c82931' } },
    { name: 'wavez', label: 'wavez', dark: true, colors: { bg: '#1c292f', main: '#6bde3b', sub: '#1f5e6b', subAlt: '#1b3238', text: '#e9efe6', error: '#ca4754' } },
    { name: 'witch_girl', label: 'witch girl', dark: false, colors: { bg: '#f3dbda', main: '#56786a', sub: '#ddb4a7', subAlt: '#e7c8be', text: '#56786a', error: '#b29a91' } },
];

declare const window: myWindow;

async function main() {
    const sess = await Session();

    const formParent = document.querySelector('#form-parent') as HTMLElement;
    const form = document.querySelector('#account-settings-form') as HTMLFormElement;
    const uploadsLabel = form.querySelector('#uploads') as HTMLLabelElement;
    const fieldset = form.querySelector('fieldset') as HTMLFieldSetElement;
    const saveBtn = document.querySelector('#save-btn') as HTMLButtonElement;

    const userID = document.querySelector('#user-id') as HTMLAnchorElement;
    const loginToken = document.querySelector('#login-token') as HTMLAnchorElement;

    userID.textContent = sess.user.id || '';
    loginToken.textContent = sess.loginToken;

    const firstName = document.querySelector('#first-name') as HTMLInputElement;
    const lastName = document.querySelector('#last-name') as HTMLInputElement;
    const signature = document.querySelector('#signature') as HTMLInputElement;
    const email = document.querySelector('#email') as HTMLInputElement;
    const $emailAliasField = document.querySelector('#email-alias-field') as HTMLElement;
    const $emailAlias = document.querySelector('#email-alias') as HTMLInputElement;
    const $emailAliasPreview = document.querySelector('#email-alias-preview') as HTMLElement;
    const phoneEl = document.querySelector('#phone') as HTMLInputElement;
    const color = document.querySelector('#color') as HTMLInputElement;
    const password = document.querySelector('#new-password') as HTMLInputElement;
    const confirmPassword = document.querySelector('#confirm-password') as HTMLInputElement;
    const $pickerBtn = document.querySelector('#theme-picker-btn') as HTMLButtonElement;
    const $pickerLabel = document.querySelector('#theme-picker-label') as HTMLSpanElement;
    const $pickerDots = document.querySelector('#theme-picker-dots') as HTMLSpanElement;
    const $pickerPopup = document.querySelector('#theme-picker-popup') as HTMLDivElement;
    const $themeSearch = document.querySelector('#theme-search') as HTMLInputElement;
    const $themeList = document.querySelector('#theme-list') as HTMLDivElement;
    const profileImage = document.querySelector('#uploader') as HTMLElement;
    const uploaderLabel = profileImage.querySelector('label') as HTMLLabelElement;
    const uploadThumbs = document.querySelector('#uploads-thumbnails') as HTMLElement;
    const img = document.querySelector('#profile-image') as HTMLImageElement;
    const quotesPagePreference = document.querySelector('#quote-page-preference') as HTMLElement;
    let uploadThumbClose: HTMLAnchorElement;

    const quotePageStyleDetailed = document.querySelector('#quote-page-one-input') as HTMLInputElement;
    const quotePageStyleRows = document.querySelector('#quote-page-two-input') as HTMLInputElement;

    // Per-browser setting (cross-domain cookie shared with legacy IAP), so it
    // applies on change rather than riding the form's Save.
    const emailOpenHelpScout = document.querySelector('#email-open-helpscout-input') as HTMLInputElement;
    emailOpenHelpScout.checked = helpscout.OpenSearchOnClick();
    emailOpenHelpScout.addEventListener('change', () => helpscout.SetOpenSearchOnClick(emailOpenHelpScout.checked));

    const blurSensitive = document.querySelector('#blur-sensitive-input') as HTMLInputElement;
    blurSensitive.checked = BlurSensitiveImages();
    blurSensitive.addEventListener('change', () => SetBlurSensitiveImages(blurSensitive.checked));

    firstName.value = sess.user.firstName || '';
    lastName.value = sess.user.lastName || '';
    email.value = sess.user.email || '';
    phoneEl.value = sess.user.phone || '';

    const editor = await ClassicEditor.create(signature);

    editor.setData(sess.user.signature || '');

    userID.addEventListener('click', e => {
        e.preventDefault();

        Copy(userID.textContent ?? '');
        const t = new bootstrap.Tooltip(userID, {
            title: 'Copied!',
            trigger: 'manual',
        });
        t.show();
        setTimeout(() => t.dispose(), 2000);
    });

    loginToken.addEventListener('click', e => {
        e.preventDefault();

        Copy(loginToken.textContent);
        const t = new bootstrap.Tooltip(loginToken, {
            title: 'Copied!',
            trigger: 'manual',
        });
        t.show();
        setTimeout(() => t.dispose(), 2000);
    });

    let hasQuotesPerm = true;
    let hasEmailAliasPerm = false;

    if (firstName.value) {
        firstName.required = true;
    }
    if (lastName.value) {
        lastName.required = true;
    }
    if (phoneEl.value) {
        phoneEl.required = true;
    }

    if (sess.user.color) {
        color.value = sess.user.color;
    }

    if (sess.userPermissions.length) {
        // The preference is only worth showing to someone who reads quotes and
        // can save it.
        if (!await HasPerm('r/quotes') ||
            !(await HasPerm('w/users/details/iapDefaultQuotesPage') || await HasPerm('w/users?user=*/details/iapDefaultQuotesPage'))) {
            quotesPagePreference.classList.add('d-none');
            hasQuotesPerm = false;
        }
        // The alias is the From on emails this user sends (see docs/emails.md).
        // Same two grants the v2 user update accepts: the self-only form the
        // Sales role holds, or the any-user form an admin holds.
        if (await HasPerm('w/users/details/emailAlias') || await HasPerm('w/users?user=*/details/emailAlias')) {
            hasEmailAliasPerm = true;
            $emailAlias.value = sess.user.userDetails?.emailAlias ?? '';
            $emailAliasField.classList.remove('d-none');

            // Preview the sender the way defaultFromIdentity picks it: the alias
            // when set, else the first name, folded to [a-z0-9._-]; an empty or
            // unparseable local part means the realm sends instead. The realm is
            // one the user's sales teams sell for — teams by name, the first with
            // a primary realm; else the realm weighting any of the teams highest
            // (name breaks ties); else Signature Coins. No address is shown until
            // that realm is known, so a failed lookup never shows a wrong one.
            const aliasLocalPart = (name: string) => name.toLowerCase().replace(/[^a-z0-9._-]/g, '');
            // Go's mail.ParseAddress takes a dot-atom local part: no leading,
            // trailing, or doubled dots.
            const usableLocalPart = (localPart: string) => /^[a-z0-9_-]+(\.[a-z0-9_-]+)*$/.test(localPart);
            const senderAddress = (realm: hyejoo.Realm, alias: string) => {
                const localPart = aliasLocalPart(alias.trim() || firstName.value);
                const address = `${localPart}@${realm.host ?? ''}`;
                // emailaddresses caps an address at 255 bytes.
                if (!usableLocalPart(localPart) || address.length > 255) {
                    return realm.email ?? '';
                }
                return address;
            };
            let senderRealm: hyejoo.Realm | 'loading' | 'failed' = 'loading';
            const renderEmailAliasPreview = () => {
                if (senderRealm === 'loading') {
                    $emailAlias.placeholder = '';
                    $emailAliasPreview.textContent = '…';
                } else if (senderRealm === 'failed') {
                    $emailAlias.placeholder = '';
                    $emailAliasPreview.textContent = 'preview unavailable';
                } else {
                    $emailAlias.placeholder = senderAddress(senderRealm, '');
                    $emailAliasPreview.textContent = senderAddress(senderRealm, $emailAlias.value);
                }
            };
            renderEmailAliasPreview();
            $emailAlias.addEventListener('input', renderEmailAliasPreview);
            firstName.addEventListener('input', renderEmailAliasPreview);

            void GetRealms({ data: 'name,host,email,salesTeamID,companyDetails', max: -1 }).then(realms => {
                const teams = (sess.user.salesTeamUsers ?? []).map(membership => membership.salesTeam).filter(team => team?.active);
                teams.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''));
                const teamIDs = new Set(teams.flatMap(team => [team?.id, team?.originalSalesTeamID]).filter(id => id));
                const primary = teams.map(team => realms.get(team?.primaryCompanyID ?? '')).find(realm => realm?.host);
                const candidates = [...realms.values()].filter(realm => realm.host).map(realm => {
                    const weights = WeightedDefaultSalesTeams(realm).filter(weighted => teamIDs.has(weighted.salesTeamID)).map(weighted => weighted.weight);
                    return { realm, weight: Math.max(0, ...weights) };
                }).filter(candidate => candidate.weight > 0);
                candidates.sort((a, b) => b.weight - a.weight || (a.realm.name ?? '').localeCompare(b.realm.name ?? ''));
                // smg.CompaniesSignatureCoins
                const picked = primary ?? candidates[0]?.realm ?? realms.get('FUGW1FiCBPI');
                senderRealm = picked?.host ? picked : 'failed';
                renderEmailAliasPreview();
            }).catch((err: unknown) => {
                senderRealm = 'failed';
                renderEmailAliasPreview();
                ShowError(err);
            });
        }
        if (sess.user.userDetails?.iapDefaultQuotesPage) {
            if (sess.user.userDetails.iapDefaultQuotesPage === '/quotes/') {
                quotePageStyleDetailed.checked = true;
                quotePageStyleRows.checked = false;
            } else if (sess.user.userDetails.iapDefaultQuotesPage === '/quotes/projects/') {
                quotePageStyleDetailed.checked = false;
                quotePageStyleRows.checked = true;
            }
        }
    }

    // Theme is stored client-side in localStorage. Migrate from backend darkMode if needed.
    const storedTheme = localStorage.getItem('theme');
    if (!storedTheme && sess.user.darkMode) {
        window.setTheme('dark');
    }

    let selectedTheme = window.getTheme();

    // --- Runtime CSS var application ---
    const expandHex = (hex: string): string => {
        if (hex.length === 4) {
            return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
        }
        return hex;
    };

    const hexToRgb = (hex: string): string => {
        const h = expandHex(hex);
        const r = parseInt(h.slice(1, 3), 16);
        const g = parseInt(h.slice(3, 5), 16);
        const b = parseInt(h.slice(5, 7), 16);
        return `${r}, ${g}, ${b}`;
    };

    const clearThemeVars = () => {
        const stored = localStorage.getItem('themeVars');
        if (stored) {
            const vars = JSON.parse(stored) as Record<string, string>;
            for (const key of Object.keys(vars)) {
                document.documentElement.style.removeProperty(key);
            }
        }
        localStorage.removeItem('themeVars');
    };

    // Mix two hex colors (0=a, 1=b)
    const mixHex = (a: string, b: string, t: number): string => {
        const ha = expandHex(a), hb = expandHex(b);
        const mix = (i: number) => {
            const va = parseInt(ha.slice(1 + i * 2, 3 + i * 2), 16);
            const vb = parseInt(hb.slice(1 + i * 2, 3 + i * 2), 16);
            return Math.round(va + (vb - va) * t);
        };
        const r = mix(0), g = mix(1), bl = mix(2);
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
    };

    const applythemeColors = (c: themeColors, persist: boolean) => {
        const root = document.documentElement;

        const sidebarBg = c.sidebarBg ?? mixHex(c.bg, '#000000', 0.4);
        const sidebarColor = c.sidebarColor ?? (IsDark(c.bg) ? c.text : '#ffffff');
        // Contrast against the accent itself, not the body bg — a light bg
        // with a light primary (e.g. yellow) still needs dark text on the accent.
        const mainRgb = CSSToRGB(c.main);
        const accentContrast = mainRgb && Luminance(mainRgb) > 0.5 ? '#000000' : '#ffffff';

        const vars: Record<string, string> = {
            '--bs-body-bg': c.bg,
            '--bs-body-bg-rgb': hexToRgb(c.bg),
            '--bs-body-color': c.text,
            '--bs-body-color-rgb': hexToRgb(c.text),
            '--bs-primary': c.main,
            '--bs-primary-rgb': hexToRgb(c.main),
            '--bs-secondary-color': c.sub,
            '--bs-secondary-color-rgb': hexToRgb(c.sub),
            '--bs-secondary-bg': c.subAlt,
            '--bs-secondary-bg-rgb': hexToRgb(c.subAlt),
            '--bs-tertiary-bg': c.subAlt,
            '--bs-tertiary-bg-rgb': hexToRgb(c.subAlt),
            '--bs-danger': c.error,
            '--bs-danger-rgb': hexToRgb(c.error),
            '--bs-emphasis-color': c.text,
            '--bs-emphasis-color-rgb': hexToRgb(c.text),
            '--bs-tertiary-color': c.sub,
            '--bs-tertiary-color-rgb': hexToRgb(c.sub),
            '--bs-border-color': c.sub,
            '--bs-border-color-translucent': `rgba(${hexToRgb(c.text)}, 0.15)`,
            '--bs-link-color': c.main,
            '--bs-link-color-rgb': hexToRgb(c.main),
            '--bs-link-hover-color': c.text,
            '--bs-link-hover-color-rgb': hexToRgb(c.text),
            '--bs-heading-color': c.text,
            '--bs-code-color': c.main,
            '--iap-sidebar-bg': sidebarBg,
            '--iap-sidebar-color': sidebarColor,
            '--iap-accent-contrast': accentContrast,
        };

        for (const key of ['success', 'warning', 'info'] as const) {
            const val = c[key];
            if (val) {
                vars[`--bs-${key}`] = val;
                vars[`--bs-${key}-rgb`] = hexToRgb(val);
            } else {
                root.style.removeProperty(`--bs-${key}`);
                root.style.removeProperty(`--bs-${key}-rgb`);
            }
        }

        for (const [key, val] of Object.entries(vars)) {
            root.style.setProperty(key, val);
        }
        if (persist) {
            localStorage.setItem('themeVars', JSON.stringify(vars));
        }
    };

    const applyTheme = (theme: themeInfo, persist: boolean) => {
        if (theme.colors) {
            const base = theme.dark ? 'dark' : 'light';
            if (persist) {
                localStorage.setItem('themeBase', base);
                window.setTheme(theme.name);
            } else {
                window.applyThemeVisual(base);
            }
            document.documentElement.setAttribute('data-iap-theme', 'custom');
            applythemeColors(theme.colors, persist);
        } else {
            clearThemeVars();
            localStorage.removeItem('themeBase');
            if (persist) {
                window.setTheme(theme.name);
            } else {
                window.applyThemeVisual(theme.name);
            }
        }
    };

    const revertToSelected = () => {
        const theme = themes.find(t => t.name === selectedTheme);
        if (theme) {
            applyTheme(theme, false);
        } else {
            clearThemeVars();
            window.applyThemeVisual(selectedTheme);
        }
    };

    // --- Picker UI ---
    const renderDots = (colors: readonly [string, string, string], container: HTMLElement) => {
        container.innerHTML = '';
        for (const c of colors) {
            const dot = document.createElement('span');
            dot.className = 'theme-dot';
            dot.style.background = c;
            container.appendChild(dot);
        }
    };

    const updatePickerButton = (theme: themeInfo) => {
        $pickerLabel.textContent = theme.label;
        renderDots(themePreviewColors(theme), $pickerDots);
    };

    let themeRows: HTMLElement[] = [];

    const buildThemeRows = () => {
        $themeList.innerHTML = '';
        themeRows = [];
        for (const theme of themes) {
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'theme-row w-100 border-0 bg-transparent px-3 py-2 text-body';
            row.dataset.theme = theme.name;
            if (theme.name === selectedTheme) {
                row.classList.add('active');
            }

            const layout = document.createElement('span');
            layout.className = 'd-flex align-items-center justify-content-between';

            const name = document.createElement('span');
            name.textContent = theme.label;
            layout.appendChild(name);

            const dots = document.createElement('span');
            dots.className = 'd-flex gap-1';
            renderDots(themePreviewColors(theme), dots);
            layout.appendChild(dots);

            row.appendChild(layout);

            row.addEventListener('mouseenter', () => {
                applyTheme(theme, false);
            });

            row.addEventListener('mouseleave', () => {
                revertToSelected();
            });

            row.addEventListener('click', () => {
                selectedTheme = theme.name;
                applyTheme(theme, true);
                const tc = theme.colors?.main;
                if (tc) {
                    color.value = tc;
                }
                updatePickerButton(theme);
                closePicker();

                for (const r of themeRows) {
                    r.classList.toggle('active', r.dataset.theme === theme.name);
                }
            });

            $themeList.appendChild(row);
            themeRows.push(row);
        }
    };

    const openPicker = () => {
        $pickerPopup.style.display = '';
        $themeSearch.value = '';
        filterThemes('');
        $themeSearch.focus();

        // Scroll active item into view
        const active = $themeList.querySelector('.theme-row.active');
        if (active) {
            active.scrollIntoView({ block: 'center' });
        }
    };

    const closePicker = () => {
        $pickerPopup.style.display = 'none';
        revertToSelected();
    };

    const filterThemes = (query: string) => {
        const q = query.toLowerCase();
        for (const row of themeRows) {
            const label = row.dataset.theme ?? '';
            const text = row.textContent?.toLowerCase() ?? '';
            row.classList.toggle('d-none', !(text.includes(q) || label.includes(q)));
        }
    };

    buildThemeRows();

    // Initialize button with current theme
    const currentthemeInfo = themes.find(t => t.name === selectedTheme) ?? themes[0];
    updatePickerButton(currentthemeInfo);

    $pickerBtn.addEventListener('click', () => {
        if ($pickerPopup.style.display === 'none') {
            openPicker();
        } else {
            closePicker();
        }
    });

    $themeSearch.addEventListener('input', () => {
        filterThemes($themeSearch.value);
    });

    // Close on outside click
    document.addEventListener('click', e => {
        const picker = document.querySelector('#theme-picker') as HTMLElement;
        if (!picker.contains(e.target as Node) && $pickerPopup.style.display !== 'none') {
            closePicker();
        }
    });

    // Close on Escape
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && $pickerPopup.style.display !== 'none') {
            closePicker();
        }
    });

    // Randomize button
    const $randomBtn = document.querySelector('#theme-random-btn') as HTMLButtonElement;
    $randomBtn.addEventListener('click', () => {
        let theme;
        do {
            theme = themes[Math.floor(Math.random() * themes.length)];
        } while (theme.name === selectedTheme && themes.length > 1);
        selectedTheme = theme.name;
        applyTheme(theme, true);
        const tc = theme.colors?.main;
        if (tc) {
            color.value = tc;
        }
        updatePickerButton(theme);
        for (const r of themeRows) {
            r.classList.toggle('active', r.dataset.theme === theme.name);
        }
    });

    let uploadID = sess.user.uploadID || 0;

    const u = new uploads.Uploader(uploadsLabel, {
        confirmBtn: saveBtn,
        thumbnails: uploadThumbs,
        companyID: models.CompanyID.COMPANIES_STERLING_MARKETING_GROUP,
        onchange: () => {
            uploadID = 0;
        },
        success: async thumb => {
            // allowPartial: success callback is unawaited; profile picture is optional
            uploadID = (await u.getUploadIDs({ allowPartial: true }))[0];
            uploadThumbs.appendChild(thumb);
            profileImage.classList.add('d-none');
            uploadThumbClose = thumb.querySelector('.upload-thumbnail-close') as HTMLAnchorElement;
            uploadThumbClose.style.cursor = 'pointer';
            uploadThumbClose.addEventListener('click', () => {
                profileImage.classList.remove('d-none');
            });
        },
        disableToast: true,
    });

    if (sess.user.upload) {
        if (sess.user.upload.key) {
            loadProfileImage(sess.user.upload);
        }
    }

    const p = new Phone(phoneEl);

    quotePageStyleDetailed.addEventListener('input', () => {
        if (quotePageStyleDetailed.checked) {
            quotePageStyleRows.checked = false;
        }
    });

    quotePageStyleRows.addEventListener('input', () => {
        if (quotePageStyleRows.checked) {
            quotePageStyleDetailed.checked = false;
        }
    });

    form.noValidate = true;
    saveBtn.addEventListener('click', async e => {
        e.preventDefault();

        if (password.value !== confirmPassword.value) {
            confirmPassword.setCustomValidity('Passwords do not match.');
        } else {
            confirmPassword.setCustomValidity('');
        }

        if (!ReportValidity(form)) {
            return false;
        }

        fieldset.disabled = true;
        buttons.Start(saveBtn);

        const currentTheme = themes.find(t => t.name === selectedTheme);
        const isDark = currentTheme?.dark === true ||
            (selectedTheme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches);

        let reloadRequired = false;
        if (sess.user.darkMode !== isDark || color.value !== sess.user.color) {
            reloadRequired = true;
        }

        sess.user.darkMode = isDark;

        const userDetails: models.IUserDetailsMod = {};
        if (hasQuotesPerm) {
            userDetails.iapDefaultQuotesPage = quotePageStyleRows.checked ? quotePageStyleRows.value : quotePageStyleDetailed.value;
        }
        if (hasEmailAliasPerm) {
            userDetails.emailAlias = $emailAlias.value.trim().toLowerCase();
        }

        const update: {
            firstName: string;
            lastName: string;
            phone: string;
            email: string;
            signature: string;
            color: string;
            darkMode: boolean;
            uploadID: number;
            password?: string;
            userDetails?: models.IUserDetailsMod;
        } = {
            firstName: firstName.value,
            lastName: lastName.value,
            phone: await p.format(),
            email: email.value,
            signature: editor.getData(),
            color: color.value,
            darkMode: isDark,
            uploadID,
        };
        if (password.value !== '') {
            update.password = password.value;
        }
        if (userDetails.iapDefaultQuotesPage !== undefined || userDetails.emailAlias !== undefined) {
            update.userDetails = userDetails;
        }
        const showFinished = () => {
            fieldset.disabled = false;
            buttons.Stop(saveBtn);
            toast.Success('Settings saved!');
        };

        axios.patch(`/v2/users/${sess.user.id ?? ''}`, update).then(() => {
            ClearCache();
            if (reloadRequired) {
                if (window.bootstrapHref) {
                    const $bootstrapLink = document.querySelector('#bootstrap-styles') as HTMLLinkElement;
                    const bootstrapHref = window.bootstrapHref + `&cache=${window.localStorage.getItem('sessionCache') ?? ''}`;

                    const link = document.createElement('link');
                    link.rel = 'stylesheet';

                    const onDone = () => {
                        link.remove();
                        $bootstrapLink.href = bootstrapHref;
                        showFinished();
                    };
                    link.addEventListener('load', onDone);
                    link.addEventListener('error', onDone);

                    link.href = bootstrapHref;
                    document.body.appendChild(link);
                } else {
                    window.location.reload();
                }
            } else {
                showFinished();
            }
        }).catch(err => {
            fieldset.disabled = false;
            buttons.Stop(saveBtn);
            ShowError(err);
        });
    });

    const spinner = document.querySelector('#form-spinner') as HTMLElement;
    spinner.classList.add('d-none');
    formParent.classList.remove('d-none');

    function loadProfileImage(upload: models.Upload) {
        const setProfileImage = document.querySelector('#set-profile-image') as HTMLElement;
        setProfileImage.classList.remove('d-none');
        const setImageClose = setProfileImage.querySelector('a') as HTMLAnchorElement;
        setImageClose.addEventListener('click', eventHandle);
        uploaderLabel.classList.add('d-none');
        img.classList.remove('d-none');
        img.src = `${window.CloudFront}/${upload.key || ''}`;

        function eventHandle() {
            setProfileImage.classList.add('d-none');
            uploaderLabel.classList.remove('d-none');
            toast.Undo('Deleted profile image.', () => {
                uploadID = sess.user.uploadID || 0;
                loadProfileImage(upload);
            });
            setImageClose.removeEventListener('click', eventHandle);
        }
    }
}

void main();